import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, resolveComponent as _resolveComponent, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, toDisplayString as _toDisplayString, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "data-status-grid" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { class: "d-flex justify-content-between align-items-end" }
const _hoisted_4 = { class: "form-check d-inline-block" }
const _hoisted_5 = {
  class: "btn-group ms-auto",
  style: {},
  role: "group"
}
const _hoisted_6 = ["href"]
const _hoisted_7 = ["href"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { key: 1 }
const _hoisted_10 = ["href"]
const _hoisted_11 = { key: 1 }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = {
  class: "btn-group",
  role: "group"
}

import {ref, computed, onMounted, watch} from "vue";
import axios from "axios";
import BGrid from "./BGrid/BGrid.vue";
import {serializeFilters, serializeSortBy} from "./BTable/HelperFunctions";
import LoadingSpinner from "./Misc/LoadingSpinner.vue";
import {BFilter} from "./BFilterEditor/BFilterEditor.types";
import {debounce} from "vue-debounce";
import {InformationModal, SystemAlerts} from "./Misc";
import {UploadType} from "@/types";
import {getHtmlContent} from "@/utils";
import {useCommunityPolicingStore} from "@/stores/communityPolicingStore";
import {useContactsStore} from "@/stores/contactsStore";
import {useNibrsStore} from "@/stores/nibrsStore";
import {useUofStore} from "@/stores/uofStore";
import {useSummaryStore} from "@/stores/summaryStore";
import {usePoliceEmployeeStore} from "@/stores/policeEmployeeStore";
import {usePopulationStore} from "@/stores/populationStore";
import DropDown from "@/components/Misc/DropDown.vue";
import {useDcraStore} from "@/stores/dcraStore";

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'ErrorsAndWarnings',
  props: {
    url: String,
    title: String,
    dataset: String,
    onlyHasOneOri: Boolean,
    enableLinkToEditor: Boolean,
    hasClearError: Boolean,
    hasClearWarning: Boolean,
    editorUrl: String,
    noTotal: Boolean,
},
  setup(__props) {

const props = __props;

// State
const dataItems = ref([]);
const numberOfReturnedItems = ref(0);
const isShowFilterEditor = ref(false);
const total = ref(0);
const showLoading = ref(false);
const showUpdateModal = ref(false);
const selectedItem = ref({});
const showErrorDescriptionModal = ref(false);
const selectedErrorDescription = ref("");
const selectedErrorCode = ref("");
const selectedErrorType = ref("");
const selectedErrorBody = ref("");
const showAdditionalColumns = ref(false);

// Store setup
const uploadType = UploadType[props.dataset ?? ""];
const store = (() => {
    switch (uploadType) {
        case UploadType.contacts:
            return useContactsStore();
        case UploadType.communityPolicing:
            return useCommunityPolicingStore();
        case UploadType.nibrs:
            return useNibrsStore();
        case UploadType.summary:
            return useSummaryStore();
        case UploadType.useOfForce:
            return useUofStore();
        case UploadType.policeEmployee:
            return usePoliceEmployeeStore();
        case UploadType.population:
            return usePopulationStore();
        case UploadType.dcra:
            return useDcraStore();
    }
})();

const apiUrl = store!.apiUrl;

const getValueFromSource = (source, target): string => {
    if (source && target) {
        let value = source[target];
        if (value) {
            return encodeURIComponent(value);
        }
    }
    return "";
};

const getEditorPageName = (incidentNumber: string, ori: string): string => {
    if (props.editorUrl != undefined) {
        var editorUrl = new URL(props.editorUrl)!;

        editorUrl.searchParams.append("incident", incidentNumber);
        editorUrl.searchParams.append("ori", ori);
        editorUrl.searchParams.append("returnurl", window.location.href);

        return editorUrl.href;
    }

    return "";
};

// Filter options
const filtersJson = sessionStorage.getItem(`ew_${uploadType}_filterOptions`);
const filterOptions = ref(
    filtersJson
        ? JSON.parse(filtersJson)
        : {
              filters: [{property: "status", operation: "eq", value: "Unresolved"}],
          }
);

// Current paging mode
const currentPagingMode = computed(() => {
    if (!props.noTotal || filterOptions.value.filters.some((a) => a.property == "status" && a.value == "Unresolved")) {
        return "withTotal";
    }
    return "noTotal";
});

// Table options
const optionsJson = sessionStorage.getItem(`ew_${uploadType}_tableOptions`);
const options = ref(
    optionsJson
        ? JSON.parse(optionsJson)
        : {
              totalItemCount: 0,
              itemsPerPage: 25,
              page: 1,
              sortBy: [],
              pagingMode: currentPagingMode.value,
              isLastPage: false,
          }
);

// Computed properties
const filterableFields = computed(() =>
    store!.getFilterableFieldsForErrorsAndWarnings(props.onlyHasOneOri, showAdditionalColumns.value, false)
);

const columns = computed(() =>
    store!.getErrorsAndWarningsColumns(props.onlyHasOneOri, showAdditionalColumns.value, false)
);

const hasAppliedFilters = ref(filterOptions.value.filters.length > 0);

// Methods
const getData = async () => {
    if (isShowFilterEditor.value) return;
   
    showLoading.value = true;
    options.value.pagingMode = currentPagingMode.value;
    const previousScroll = window.scrollY;

    try {
        const response = await store!.getErrorsAndWarnings(
            filterOptions.value.filters,
            options.value,
            filterableFields.value,
            options.value.sortBy
        );

        const pagingMode = response?.data?.pagingMode ?? currentPagingMode.value;
        if (pagingMode == "noTotal" && response?.data?.data?.length > 0) {
            numberOfReturnedItems.value = response!.data.data.length;
            dataItems.value = response!.data.data.slice(0, options.value.itemsPerPage);
        } else {
            dataItems.value = response!.data.data;
        }

        total.value = response!.data.total;
        options.value.totalItemCount = total.value;

        window.scrollTo({
            top: previousScroll,
            behavior: 'smooth'
        });
    } catch (error) {
        console.error(error?.response?.statusText);
    } finally {
        showLoading.value = false;
        updateTableDataSource();
    }
};

const handleUpdateModalDismiss = async () => {
    showUpdateModal.value = false;
    await getData();
};

const updateTableDataSource = debounce(() => getData(), "30000ms");

const isStatusLinkEnabled = (item: any) => {
    return (
        (item["errorType"] == "Warning" && props.hasClearWarning) ||
        (item["errorType"] == "Error" && props.hasClearError)
    );
};

const exportData = (fileType: string) => {
    let filters = [] as BFilter[];
    if (hasAppliedFilters.value) {
        filters = filterOptions.value.filters;
    }

    let filterText = serializeFilters(filters, filterableFields.value);
    let sortText = serializeSortBy(options.value.sortBy);

    var fullUrl = props.url;
    fullUrl += `export/${fileType}`;
    axios
        .get(fullUrl!, {
            responseType: "arraybuffer",
            params: {filter: filterText, sort: sortText},
        })
        .then(function (response) {
            var blob = new Blob([response.data], {
                type: response.headers["content-type"],
            });
            var link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = response.headers["content-disposition"].substring(
                response.headers["content-disposition"].indexOf("filename=") + 9
            );
            link.click();
        });
};

const handleStatusClick = (item: any) => {
    if (item != null) {
        var idNumber = props.dataset == "communityPolicing" ? item["stopNumber"] : item["incidentNumber"];

        selectedItem.value = item;
        showUpdateModal.value = true;
    }
};

const handleErrorCodeClick = (item: any) => {
    let errorCode = item["errorCode"];
    selectedErrorCode.value = "";
    selectedErrorDescription.value = "";
    selectedErrorBody.value = "No information available";

    if (errorCode != undefined && store!.errorDescriptions && store!.errorDescriptions.value) {
        selectedErrorCode.value = errorCode;
        store!.errorDescriptions.value.forEach((f) => {
            if (f.code == errorCode) {
                selectedErrorType.value = item["errorType"];
                selectedErrorDescription.value = f.help;

                var descriptionText = "";

                getHtmlContent(`help.aspx?e=${selectedErrorCode.value}`)
                    .then((data) => (descriptionText = data))
                    .catch((err) => (descriptionText = ""))
                    .finally(() => {
                        if (descriptionText != "") {
                            selectedErrorBody.value = descriptionText;
                        } else if (selectedErrorDescription.value != "") {
                            selectedErrorBody.value = `${selectedErrorDescription.value}`;
                        } else {
                            selectedErrorBody.value = "No information available";
                        }
                        showErrorDescriptionModal.value = true;
                        return;
                    });
            }
        });
        showErrorDescriptionModal.value = true;
    }
};

// Watchers
watch(
    () => options.value,
    () => {
        sessionStorage.setItem(`ew_${uploadType}_tableOptions`, JSON.stringify(options.value));
    },
    {deep: true}
);

watch(
    () => filterOptions.value,
    () => {
        sessionStorage.setItem(`ew_${uploadType}_filterOptions`, JSON.stringify(filterOptions.value));
        options.value.pagingMode = currentPagingMode.value;
    },
    {deep: true}
);

// Lifecycle hooks
onMounted(() => {
    getData();
});

return (_ctx: any,_cache: any) => {
  const _component_errors_warnings_update = _resolveComponent("errors-warnings-update")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LoadingSpinner, { "show-loading": showLoading.value }, null, 8, ["show-loading"]),
    _createVNode(_unref(InformationModal), {
      title: selectedErrorCode.value,
      modelValue: showErrorDescriptionModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showErrorDescriptionModal).value = $event))
    }, {
      body: _withCtx(() => [
        _createElementVNode("div", { innerHTML: selectedErrorBody.value }, null, 8, _hoisted_2)
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_errors_warnings_update, {
      modelValue: showUpdateModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showUpdateModal).value = $event)),
      item: selectedItem.value,
      url: `${_unref(apiUrl)}/actions/dismiss-error-warning`,
      dataset: __props.dataset,
      onDismiss: handleUpdateModalDismiss
    }, null, 8, ["modelValue", "item", "url", "dataset"]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _withDirectives(_createElementVNode("input", {
          id: "cbAddCol",
          type: "checkbox",
          class: "form-check-input",
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showAdditionalColumns).value = $event))
        }, null, 512), [
          [_vModelCheckbox, showAdditionalColumns.value]
        ]),
        _cache[5] || (_cache[5] = _createElementVNode("label", {
          for: "cbAddCol",
          class: "form-check-label"
        }, "Display with the latest explanation/justification for Status change", -1))
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(DropDown, {
          label: "Error/Warning Details",
          "item-count": 2,
          "is-primary-button": false
        }, {
          item1: _withCtx(({}) => [
            _createElementVNode("a", {
              class: "dropdown-item",
              href: `${_unref(store).errorDescriptionsDownloadUrl}csv`
            }, "Download as .csv", 8, _hoisted_6)
          ]),
          item2: _withCtx(({}) => [
            _createElementVNode("a", {
              class: "dropdown-item",
              href: `${_unref(store).errorDescriptionsDownloadUrl}xlsx`
            }, "Download as .xlsx", 8, _hoisted_7)
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(BGrid, {
      caption: "List of errors and warnings",
      tableOptions: options.value,
      filterableFields: filterableFields.value,
      filterOptions: filterOptions.value,
      onOptionChange: getData,
      items: dataItems.value,
      columns: columns.value,
      loading: false,
      "current-item-count": numberOfReturnedItems.value
    }, {
      "cell.status": _withCtx(({item, column}) => [
        (isStatusLinkEnabled(item))
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start",
              onClick: _withModifiers(($event: any) => (handleStatusClick(item)), ["prevent"])
            }, _toDisplayString(item.status), 9, _hoisted_8))
          : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(item.status), 1))
      ]),
      "cell.incidentNumber": _withCtx(({item}) => [
        (__props.enableLinkToEditor)
          ? (_openBlock(), _createElementBlock("a", {
              key: 0,
              class: "btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start",
              href: 
                        getEditorPageName(getValueFromSource(item, 'incidentNumber'), getValueFromSource(item, 'ori'))
                    
            }, _toDisplayString(item["incidentNumber"]), 9, _hoisted_10))
          : _createCommentVNode("", true),
        (!__props.enableLinkToEditor)
          ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(item["incidentNumber"]), 1))
          : _createCommentVNode("", true)
      ]),
      "cell.errorCode": _withCtx(({item}) => [
        _createElementVNode("button", {
          type: "button",
          class: "btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start",
          onClick: _withModifiers(($event: any) => (handleErrorCodeClick(item)), ["prevent"])
        }, _toDisplayString(item["errorCode"]), 9, _hoisted_12)
      ]),
      "cell.vendorName": _withCtx(({item}) => [
        _createElementVNode("p", null, _toDisplayString(item.vendorName == null || item.vendorName?.length == 0 ? "Vendor Not Specified": item.vendorName), 1)
      ]),
      pagingStart: _withCtx(() => [
        _createElementVNode("div", _hoisted_13, [
          _createVNode(DropDown, {
            label: "Export Data",
            "item-count": 2,
            "is-primary-button": true
          }, {
            item1: _withCtx(({item}) => [
              _createElementVNode("a", {
                class: "dropdown-item",
                href: "javascript:void",
                onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (exportData('csv')), ["prevent"]))
              }, "Download as .csv")
            ]),
            item2: _withCtx(({item}) => [
              _createElementVNode("a", {
                class: "dropdown-item",
                href: "javascript:void",
                onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (exportData('xlsx')), ["prevent"]))
              }, "Download as .xlsx")
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["tableOptions", "filterableFields", "filterOptions", "items", "columns", "current-item-count"]),
    _createVNode(_unref(SystemAlerts))
  ]))
}
}

})